// react
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import { Box, Typography } from '@mui/material';

// api
import {
  useGetBasicEnergyAccountQuery,
  usePostBasicEnergyAccountMutation,
  useUpdateBasicEnergySubscriptionMutation,
} from '@/api/energy-analysis/basicEnergySlice';
import {
  useAddServiceToAccountMutation,
  useGetAccountByIdQuery,
  useRemoveServiceFromAccountMutation,
} from '@/api/recalc/endpoints/accountsApiSlice';

// shared components
import CustomSwitch from '@/components/ui/switch/CustomSwitch';

// components
import { showSnackbar } from '@/redux/snackbarSlice';
import { servicesList } from '@/utils/services';

interface ServiceActivationSwitchProps {
  managedAccountId: string | number;
  servicePath: string;
  ariaLabel: string;
  basicEnergyType?: 'Power' | 'Heat' | 'Water';
}

interface BasicEnergySubscription {
  AccountId: string;
  Power: boolean;
  Heat: boolean;
  Water: boolean;
}

type ErrorWithStatus = {
  status?: number | string;
};

const hasStatus = (error: unknown): error is ErrorWithStatus => {
  return typeof error === 'object' && error !== null && 'status' in error;
};

const ServiceActivationSwitch = ({
  managedAccountId,
  servicePath,
  ariaLabel,
  basicEnergyType,
}: ServiceActivationSwitchProps) => {
  const { t } = useTranslation('service-manager');
  const dispatch = useDispatch();

  const { data: account } = useGetAccountByIdQuery(managedAccountId);
  const [addServiceToAccount, { isLoading: isAddingService }] =
    useAddServiceToAccountMutation();
  const [removeServiceFromAccount, { isLoading: isRemovingService }] =
    useRemoveServiceFromAccountMutation();

  const { data: basicEnergySubscription, error: basicEnergySubscriptionError } =
    useGetBasicEnergyAccountQuery(
      { account_id: managedAccountId },
      { skip: !basicEnergyType || !managedAccountId },
    );

  const [createBasicEnergySubscription] = usePostBasicEnergyAccountMutation();
  const [updateBasicEnergySubscription] =
    useUpdateBasicEnergySubscriptionMutation();

  const service = servicesList.find(
    (serviceItem) => serviceItem.path === servicePath,
  );

  const setBasicEnergyTypeState = async (enabled: boolean): Promise<void> => {
    if (!basicEnergyType) return;

    const defaultSubscription: Omit<BasicEnergySubscription, 'AccountId'> = {
      Power: false,
      Heat: false,
      Water: false,
    };

    if (basicEnergySubscription) {
      if (basicEnergySubscription[basicEnergyType] === enabled) return;

      await updateBasicEnergySubscription({
        account_id: basicEnergySubscription.AccountId,
        body: {
          ...basicEnergySubscription,
          [basicEnergyType]: enabled,
        },
      }).unwrap();
      return;
    }

    const statusCode = hasStatus(basicEnergySubscriptionError)
      ? basicEnergySubscriptionError.status
      : undefined;

    if (enabled && statusCode === 404) {
      await createBasicEnergySubscription({
        AccountId: managedAccountId,
        ...defaultSubscription,
        [basicEnergyType]: true,
      }).unwrap();
      return;
    }

    if (enabled) {
      await createBasicEnergySubscription({
        AccountId: managedAccountId,
        ...defaultSubscription,
        [basicEnergyType]: true,
      }).unwrap();
    }
  };

  const isEnabled =
    !!service &&
    !!account?.Services?.some(
      (accountService) => accountService.Id === service.Id,
    );

  const handleToggle = async (enable: boolean) => {
    if (!service?.Id) return;

    if (enable) {
      const result = await addServiceToAccount({
        accountId: managedAccountId,
        serviceId: service.Id,
      });

      if (!('error' in result)) {
        await setBasicEnergyTypeState(true);

        dispatch(
          showSnackbar({
            message: t('serviceList.messages.serviceAdded'),
            severity: 'success',
          }),
        );
      }
      // Error handled by apiErrorMiddleware (snackbar + App Insights).
      return;
    }

    const result = await removeServiceFromAccount({
      accountId: managedAccountId,
      serviceId: service.Id,
    });

    if (!('error' in result)) {
      await setBasicEnergyTypeState(false);

      dispatch(
        showSnackbar({
          message: t('serviceList.messages.serviceRemoved'),
          severity: 'success',
        }),
      );
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <Box display="flex" alignItems="center">
      <Typography variant="body2" color="text.secondary" mr={1}>
        {isEnabled
          ? t('serviceList.enabled', 'Active on Account')
          : t('serviceList.disabled', 'Inactive on Account')}
      </Typography>
      <CustomSwitch
        checked={isEnabled}
        size="small"
        disabled={!service || isAddingService || isRemovingService}
        onChange={(event) => {
          void handleToggle(event.target.checked);
        }}
        inputProps={{
          'aria-label': ariaLabel,
        }}
      />
    </Box>
  );
};

export default ServiceActivationSwitch;
